import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusCircle, X, Upload } from "lucide-react";
import { useMutation, gql } from "@apollo/client";

const ADD_HEARING = gql`
  mutation AddHearing(
    $odrnumber: Int!
    $bookingNumber: String!
    $caseNumber: String!
	$participant: Int!
    $hearing: AddHearingInput!
  ) {
    addHearing(
      odrnumber: $odrnumber
      bookingNumber: $bookingNumber
      caseNumber: $caseNumber
	  participant: $participant
      hearing: $hearing
    ) {
      message
    }
  }
`;

const HEARING_DETAIL_OPTIONS = [
  { value: "suitability", label: "Suitability" },
  { value: "reinstatement", label: "Reinstatement" },
  { value: "progress report", label: "Progress Report" },
];

const HEARING_TYPE_OPTIONS = [
  { value: "in person", label: "In Person" },
  { value: "virtual", label: "Virtual" },
  { value: "non-appearance", label: "Non-Appearance" },
  // { value: "provider choice", label: "Provider Choice" },
];

const normalizeHearingTypeForSave = (hearingType: string) => {
  const rawHearingType = String(hearingType || "").trim();
  const normalized = rawHearingType
    .toLowerCase()
    .replace(/['`\u2019]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return normalized === "provider choice" || normalized === "provider s choice" || normalized === "providers choice"
    ? "non-appearance"
    : rawHearingType;
};

const COURT_ORDER_OPTIONS = [
  "Residential SUD Tx",
  "IOP SUD Tx",
  "Anger Management",
  "Domestic Violence",
  "Residential SUD Tx Directly from Custody",
  "LAI",
];

export const AddHearingModal = ({
  isOpen,
  onClose,
  odrNumber,
  bookingNumber,
  caseNumber,
  refetch,
  participant,
}: {
  isOpen: boolean;
  onClose: () => void;
  odrNumber: number;
  bookingNumber: string;
  caseNumber: string;
  refetch: () => void;
  participant: number;
}) => {
  const [formData, setFormData] = useState({
    hearingDate: "",
    detailType: "suitability",
    reinstatementNumber: "1",
    hearingType: "",
    minutes: null as File | null,
    courtOrders: [""],
  });
  const [hearingTypeError, setHearingTypeError] = useState("");

  const [addHearing] = useMutation(ADD_HEARING);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === "hearingType") {
      setHearingTypeError("");
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, minutes: file }));
    }
  };

  const handleCourtOrderChange = (index: number, value: string) => {
    setFormData((prev) => {
      const newCourtOrders = [...prev.courtOrders];
      newCourtOrders[index] = value;
      return { ...prev, courtOrders: newCourtOrders };
    });
  };

  const addCourtOrder = () => {
    setFormData((prev) => ({
      ...prev,
      courtOrders: [...prev.courtOrders, ""],
    }));
  };

  const removeCourtOrder = (index: number) => {
    if (formData.courtOrders.length > 1) {
      setFormData((prev) => ({
        ...prev,
        courtOrders: prev.courtOrders.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let minutesPayload = null;
      if (formData.minutes) {
        const reader = new FileReader();
        const minutesContent = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(formData.minutes as File);
        });

        const base64Contents = String(minutesContent || "").split(",").pop() || "";
        minutesPayload = {
          filename: formData.minutes.name,
          content: base64Contents,
        };
      }

      const filteredCourtOrders = formData.courtOrders.filter(
        (order) => order.trim() !== ""
      );
      const hearingType = normalizeHearingTypeForSave(formData.hearingType);

      if (!hearingType) {
        setHearingTypeError("Hearing type is required.");
        return;
      }

      await addHearing({
        variables: {
          odrnumber: odrNumber,
          bookingNumber: bookingNumber,
          caseNumber: caseNumber,
		  participant: participant,
          hearing: {
            hearingDate: formData.hearingDate,
            details: formData.detailType,
            type: hearingType,
            detailType: formData.detailType,
            reinstatementNumber: formData.detailType === "reinstatement" ? formData.reinstatementNumber : null,
            minutes: minutesPayload,
            courtOrders: filteredCourtOrders,
          },
        },
      });
      refetch();
      onClose();
    } catch (error) {
      console.error("Error adding hearing:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            Add New Hearing
          </DialogTitle>
        </DialogHeader>

	        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hearingDate">Next Hearing Date</Label>
              <Input
                type="date"
                id="hearingDate"
                name="hearingDate"
                value={formData.hearingDate}
                onChange={handleInputChange}
                required
              />
            </div>

	            <div className="space-y-2">
              <Label htmlFor="detailType">Hearing Details</Label>
              <Select
                value={formData.detailType}
                onValueChange={(value) =>
                  handleSelectChange("detailType", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {HEARING_DETAIL_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hearingType">Hearing Type <span className="text-red-600">*</span></Label>
              <Select
                value={formData.hearingType}
                onValueChange={(value) =>
                  handleSelectChange("hearingType", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {HEARING_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hearingTypeError && (
                <p className="text-sm text-red-600">{hearingTypeError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="minutes">Minutes (File Upload)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  id="minutes"
                  name="minutes"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("minutes")?.click()}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {formData.minutes ? formData.minutes.name : "Upload Minutes"}
                </Button>
              </div>
            </div>
          </div>

          {formData.detailType === "reinstatement" && (
            <div className="space-y-2">
              <Label htmlFor="reinstatementNumber">Reinstatement Number</Label>
              <Select
                value={formData.reinstatementNumber}
                onValueChange={(value) =>
                  handleSelectChange("reinstatementNumber", value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["1", "2", "3", "4", "5", "6"].map((value) => (
                    <SelectItem key={value} value={value}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label>Court Orders</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCourtOrder}
                className="flex items-center gap-2"
              >
                <PlusCircle className="h-4 w-4" />
                Add Court Order
              </Button>
            </div>

            <div className="space-y-2">
              {formData.courtOrders.map((order, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <Input
                    value={order}
                    onChange={(e) =>
                      handleCourtOrderChange(index, e.target.value)
                    }
                    list="legacy-court-order-options"
                    placeholder="Select or enter court order"
                    className="flex-1"
                  />
                  {formData.courtOrders.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCourtOrder(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <datalist id="legacy-court-order-options">
              {COURT_ORDER_OPTIONS.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="submit">Add Hearing</Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
